import type { Phone } from '../types'
import { statusConfig } from '../config/statusConfig'
import { ownerLabel } from '../lib/ownerLabel'
import { BatteryCell, SignalCell, SimCell } from './MetricCell'
import { PermissionsCell } from './PermissionsCell'
import { DASH, formatRelativeTime, formatUptime } from '../lib/metricFormat'
import { PhoneActions, type PhoneActionsCallbacks } from './PhoneActions'

interface PhoneMobileCardProps extends PhoneActionsCallbacks {
  phone: Phone
}

/**
 * Mobile-only card (md:hidden in DashboardPage). Same data as PhoneRow
 * stacked vertically.
 */
export function PhoneMobileCard({ phone, ...actions }: PhoneMobileCardProps) {
  const sc = statusConfig[phone.status]
  const inactive = phone.isActive ? '' : 'opacity-50'
  const owner = ownerLabel(phone)
  const m = phone.metrics
  const uptime = formatUptime(m?.uptimeSeconds ?? null)
  const updated = m?.metricsUpdatedAt ? formatRelativeTime(m.metricsUpdatedAt) : DASH
  return (
    <div
      data-testid={`phone-mobile-${phone.id}`}
      className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 ${inactive}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${sc.dot}`} />
            <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">{phone.name}</h3>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{phone.sim}</div>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{sc.label}</span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <SimCell phone={phone} />
        <SignalCell phone={phone} />
        <PermissionsCell phone={phone} />
        <BatteryCell phone={phone} />
      </div>

      {owner && (
        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 flex flex-wrap gap-x-2">
          <span>{owner}</span>
        </div>
      )}

      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        Аптайм: {uptime} <span className="text-gray-400 dark:text-gray-500">• {updated}</span>
        {m?.appVersion && (
          <span className="text-gray-400 dark:text-gray-500"> • v{m.appVersion}</span>
        )}
      </div>

      {(m?.phoneModel || m?.osVersion) && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {[m?.phoneModel, m?.osVersion && `Android ${m.osVersion}`]
            .filter(Boolean)
            .join(' • ')}
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
        <PhoneActions phone={phone} {...actions} />
      </div>
    </div>
  )
}
